import React, { useState } from "react";
import { Card, Form, Input, Button, Typography, Switch } from "antd";
import { LockOutlined, UserOutlined, BulbOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from 'react-toastify';
import { login } from "./authService";
import { useTheme } from "Common/hooks/context/ThemeProvider";

const { Title, Text } = Typography;

const Login = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { darkMode, toggleTheme } = useTheme();

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const data = await login(values.email, values.password);
      const role = data?.roles?.[0];

      localStorage.setItem("userId", data.id);
      localStorage.setItem("personalId", data.personalId);
      localStorage.setItem("token", data.token);
      localStorage.setItem("role", role);
      localStorage.setItem("presenceHubStarted", "true");

      toast.success(`Welcome ${data?.fullName || "back"}! Login successful.`);

      const rolePaths = {
        SuperAdmin: "/proAdmin-dashboard",
        Admin: "/admin-dashboard",
        Mentor: "/mentor-dashboard",
        Learner: "/learner-dashboard",
        Manager: "/manager-dashboard",
        SelfLearner: "/selfLearner-dashboard",
        Expert:"/expert-dashboard"
      };

      // Save the platform type
      localStorage.setItem("platformUserType", data.platformUserType);

      if (data.platformUserType === 0) {
        // Organization Users (Admin, Mentor, Learner, Manager)
        navigate(rolePaths[role] || "/");
      } else if (data.platformUserType === 1) {
        // Individual Users (SelfLearner or CORIQPRO_Admin)
        if (role === "CORIQPRO_Admin") {
          navigate(rolePaths.SuperAdmin);
        } else if (role === "SelfLearner") {
          navigate(rolePaths.SelfLearner);
        }
        else if (role === "Expert") {
          navigate(rolePaths.Expert);
        }
        else {
          navigate("/"); // fallback
        }
      }

      window.location.reload();
    } catch (error) {
      console.error("Login failed:", error);
      
      // Use toast for all error types
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (typeof errorData === 'string') {
          toast.error(errorData);
        } else if (errorData.message) {
          toast.error(errorData.message);
        } else if (errorData.errors) {
          // Handle validation errors
          const errorMessages = Object.values(errorData.errors).flat();
          errorMessages.forEach(msg => toast.error(msg));
        } else if (errorData.title) {
          toast.error(errorData.title);
        } else {
          toast.error("Invalid email or password");
        }
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ ...styles.container, backgroundColor: darkMode ? "#1e1e2f" : "#f4f6f9" }}>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Card
          style={{
            ...styles.card,
            backgroundColor: darkMode ? "#2d2d3a" : "#fff",
            color: darkMode ? "#fff" : "#000",
          }}
          bordered={false}
        >
          <div style={styles.header}>
            <Title level={3} style={{ color: darkMode ? "#fff" : "#1890ff" }}>
              Welcome to CORIQPRO
            </Title>
            <div style={styles.themeToggle}>
              <BulbOutlined style={{ marginRight: 8 }} />
              <Switch checked={darkMode} onChange={toggleTheme} />
            </div>
          </div>

          <Text
            type="secondary"
            style={{ display: "block", marginBottom: 24, color: darkMode ? "#ccc" : undefined }}
          >
            Please log in to continue
          </Text>

          <Form name="login-form" layout="vertical" onFinish={handleLogin}>
            <Form.Item
              name="email"
              label={<span style={{ color: darkMode ? "#fff" : "#000" }}>Email</span>}
              rules={[
                { required: true, message: "Please enter your email!" },
                { type: "email", message: "Enter a valid email!" },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="example@CORIQPRO.com"
                autoComplete="email"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={<span style={{ color: darkMode ? "#fff" : "#000" }}>Password</span>}
              rules={[
                { required: true, message: "Please enter your password!" },
                { min: 8, message: "Password must be at least 8 characters!" },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Enter your password"
                autoComplete="current-password"
              />
            </Form.Item>

            <Form.Item>
              <motion.div
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 20 }}
              >
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{
                    backgroundColor: darkMode ? "#226c87c7" : "#0c313f",
                    borderColor: darkMode ? "#226c87c7" : "#0c313f",
                    color: "#fff",
                    transition: "all 0.3s ease",
                  }}
                  block
                  loading={loading}
                  disabled={loading}
                >
                  {loading ? "Logging in..." : "Login"}
                </Button>
              </motion.div>
            </Form.Item>
          </Form>

          <div style={{ marginTop: 24, textAlign: "center" }}>
            <Text type="secondary" style={{ color: darkMode ? "#aaa" : undefined }}>
              New to CORIQPRO?
            </Text>
            <br />
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => navigate("/startup")}
            >
              Create New Account
            </Button>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    transition: "background-color 0.3s ease",
  },
  card: {
    width: "120%",
    maxWidth: "500px",
    padding: "40px",
    borderRadius: "12px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
    transition: "background-color 0.3s ease, color 0.3s ease",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  themeToggle: {
    display: "flex",
    alignItems: "center",
  },
};

export default Login;